/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { AVAILABLE_TOOLS } from './tools';
import { FunctionDeclaration, FunctionResponseScheduling, GroundingChunk } from '@google/genai';
import { DEFAULT_VOICE } from './constants';

const defaultAgents: Agent[] = [
    {
      id: 'priya-sharma-support',
      name: 'Priya Sharma',
      description: 'A friendly and efficient agent for handling customer queries.',
      status: 'active',
      language: 'English',
      industry: 'Customer Support',
      voice: 'Kore',
      allowInterruptions: true,
      noiseReduction: true,
      systemPrompt: 'You are Priya, a customer support agent. Be helpful, polite, and concise.',
      formality: 70,
      enthusiasm: 40,
      knowledgeBase: [{ type: 'url', url: 'https://example.com/faq' }],
      model: 'gemini-2.5-flash',
      temperature: 0.7,
      topP: 1,
      tools: AVAILABLE_TOOLS,
    },
    {
      id: 'sales-agent-sameer',
      name: 'Sameer Khan',
      description: 'A persuasive and knowledgeable sales agent to drive conversions.',
      status: 'active',
      language: 'English',
      industry: 'Sales',
      voice: 'Puck',
      allowInterruptions: true,
      noiseReduction: true,
      systemPrompt: 'You are Sameer, a sales agent. Your goal is to understand customer needs and recommend the right products.',
      formality: 60,
      enthusiasm: 70,
      knowledgeBase: [],
      model: 'gemini-2.5-flash',
      temperature: 0.8,
      topP: 1,
      tools: AVAILABLE_TOOLS,
    },
    {
      id: 'healthcare-assistant-olivia',
      name: 'Olivia Chen',
      description: 'An empathetic and organized assistant for healthcare inquiries.',
      status: 'active',
      language: 'English',
      industry: 'Healthcare',
      voice: 'Zephyr',
      allowInterruptions: true,
      noiseReduction: true,
      systemPrompt: 'You are Olivia, a healthcare assistant. You are helpful, patient, and must handle information with care.',
      formality: 80,
      enthusiasm: 30,
      knowledgeBase: [],
      model: 'gemini-2.5-flash',
      temperature: 0.6,
      topP: 1,
      tools: [],
    }
];

// UI State
interface UIState {
  isSidebarOpen: boolean;
  isSidebarMinimized: boolean;
  toggleSidebar: () => void;
  toggleSidebarMinimized: () => void;
  editingAgentId: string | null;
  setEditingAgentId: (id: string | null) => void;
  selectedAgentId: string | null;
  setSelectedAgentId: (id: string | null) => void;
}

export const useUI = create<UIState>((set) => ({
  isSidebarOpen: typeof window !== 'undefined' && window.innerWidth > 1024, // Open on desktop, closed on mobile by default
  isSidebarMinimized: false,
  toggleSidebar: () => set((state) => ({ 
    isSidebarOpen: !state.isSidebarOpen,
    isSidebarMinimized: false // Reset to normal when opening
  })),
  toggleSidebarMinimized: () => set((state) => ({ 
    isSidebarMinimized: !state.isSidebarMinimized 
  })),
  editingAgentId: null,
  setEditingAgentId: (id) => set({ editingAgentId: id }),
  selectedAgentId: defaultAgents[0]?.id || null,
  setSelectedAgentId: (id) => set({ selectedAgentId: id }),
}));

// Agent State
export interface FunctionCall {
  name: string;
  description?: string;
  parameters?: {
    properties?: Record<string, any>;
    required?: string[];
  };
  isEnabled: boolean;
  scheduling?: FunctionResponseScheduling;
}

export interface KnowledgeBaseItem {
  type: 'file' | 'url';
  name?: string;
  size?: string;
  url?: string;
  status?: 'processing' | 'processed' | 'error';
  smartCSVId?: string;
  summary?: string;
  metadata?: string;
  ragEnabled?: boolean;
  ragSize?: number;
  ragMonthlyCost?: number;
}

export interface Agent {
  id: string;
  name: string;
  description: string;
  status: 'active' | 'inactive';
  language: string;
  industry: string;
  voice: string;
  allowInterruptions: boolean;
  noiseReduction: boolean;
  systemPrompt: string;
  firstMessage?: string; // AI's first message when user calls
  formality: number;
  enthusiasm: number;
  knowledgeBase: KnowledgeBaseItem[];
  model: string;
  temperature: number;
  topP: number;
  tools: FunctionCall[];
  integrationAccess?: Record<string, boolean>;
  twilioPhoneNumber?: string; // Assigned Twilio phone number for this agent
}

interface AgentState {
  agents: Agent[];
  addAgent: (agentConfig: Omit<Agent, 'id' | 'status'>) => void;
  updateAgent: (id: string, updates: Partial<Agent>) => void;
  deleteAgent: (id: string) => void;
}

export const useAgentStore = create<AgentState>()(
  persist(
    (set) => ({
      agents: defaultAgents,
      addAgent: (agentConfig) =>
        set((state) => ({
          agents: [
            ...state.agents,
            {
              ...agentConfig,
              id: crypto.randomUUID(),
              status: 'active',
            },
          ],
        })),
      updateAgent: (id, updates) =>
        set((state) => ({
          agents: state.agents.map((agent) =>
            agent.id === id ? { ...agent, ...updates } : agent
          ),
        })),
      deleteAgent: (id) =>
        set((state) => ({
          agents: state.agents.filter((agent) => agent.id !== id),
        })),
    }),
    {
      name: 'agent-storage',
    }
  )
);

// Log State
export interface ConversationTurn {
    role: 'user' | 'agent' | 'system';
    text: string;
    isFinal: boolean;
    timestamp: Date;
    groundingChunks?: GroundingChunk[];
}

interface LogState {
    turns: ConversationTurn[];
    addTurn: (turn: Omit<ConversationTurn, 'timestamp'>) => void;
    updateLastTurn: (updates: Partial<ConversationTurn>) => void;
    clearTurns: () => void;
}

export const useLogStore = create<LogState>((set) => ({
    turns: [],
    addTurn: (turn) => set((state) => ({
        turns: [...state.turns, { ...turn, timestamp: new Date() }],
    })),
    updateLastTurn: (updates) => set((state) => {
        const newTurns = [...state.turns];
        if (newTurns.length > 0) {
            newTurns[newTurns.length - 1] = { ...newTurns[newTurns.length - 1], ...updates };
        }
        return { turns: newTurns };
    }),
    clearTurns: () => set({ turns: [] }),
}));

// Call History State
export interface Call {
  id: string;
  agentId: string;
  agentName: string;
  startTime: Date;
  endTime: Date;
  summary: string;
  transcript: ConversationTurn[];
  audioUrl?: string;
  satisfaction: {
    score: number; // 1-10
    justification: string;
  };
}

interface CallHistoryState {
  calls: Call[];
  addCall: (call: Call) => void;
}

const dummyAgentsForHistory = [
  { id: 'priya-sharma-support', name: 'Priya Sharma' },
  { id: 'sales-agent-sameer', name: 'Sameer Khan' },
  { id: 'healthcare-assistant-olivia', name: 'Olivia Chen' },
  { id: 'arjun-mehta-support', name: 'Arjun Mehta' },
  { id: 'emily-white-sales', name: 'Emily White' },
];

const createDummyCallHistory = (): Call[] => {
  const calls: Call[] = [];
  const now = new Date();
  for (let i = 0; i < 15; i++) {
    const agent = dummyAgentsForHistory[i % dummyAgentsForHistory.length];
    const startTime = new Date(now.getTime() - Math.random() * 7 * 24 * 60 * 60 * 1000); // within last 7 days
    const endTime = new Date(startTime.getTime() + (Math.random() * 5 + 1) * 60 * 1000); // 1-6 min duration
    const transcript: ConversationTurn[] = [
      { role: 'user', text: 'Hello, I have a question about my recent order.', isFinal: true, timestamp: startTime },
      { role: 'agent', text: 'Of course, I can help with that. What is your order number?', isFinal: true, timestamp: new Date(startTime.getTime() + 5000) },
      { role: 'user', text: 'My order number is 12345.', isFinal: true, timestamp: new Date(startTime.getTime() + 10000) },
      { role: 'agent', text: 'Thank you. I see your order has been shipped and should arrive tomorrow.', isFinal: true, timestamp: endTime },
    ];
    calls.push({
      id: `call-${i}`,
      agentId: agent.id,
      agentName: agent.name,
      startTime,
      endTime,
      summary: 'The user inquired about the status of their order. The agent confirmed the order has been shipped and is expected to arrive tomorrow.',
      transcript,
      satisfaction: {
        score: parseFloat(((Math.random() * 2) + 8).toFixed(1)), // Score between 8.0-10.0
        justification: 'The agent was helpful and resolved the query quickly.',
      },
    });
  }
  return calls;
};

export const useCallHistoryStore = create<CallHistoryState>()(
  persist(
    (set) => ({
      calls: createDummyCallHistory(),
      addCall: (call) => set((state) => ({ calls: [call, ...state.calls] })),
    }),
    {
      name: 'call-history-storage',
      // Custom serialization to handle Date objects
      storage: {
        getItem: (name) => {
          const str = localStorage.getItem(name);
          if (!str) return null;
          const { state } = JSON.parse(str);
          return {
            state: {
              ...state,
              calls: state.calls.map((call: Call) => ({
                ...call,
                startTime: new Date(call.startTime),
                endTime: new Date(call.endTime),
                transcript: call.transcript.map(turn => ({
                  ...turn,
                  timestamp: new Date(turn.timestamp),
                })),
              })),
            },
          };
        },
        setItem: (name, newValue) => {
          // The value is already stringified by persist middleware
          localStorage.setItem(name, JSON.stringify(newValue));
        },
        removeItem: (name) => localStorage.removeItem(name),
      },
    }
  )
);


// Connection State
interface ConnectionState {
    connected: boolean;
    setConnected: (connected: boolean) => void;
}

export const useConnectionStore = create<ConnectionState>((set) => ({
    connected: false,
    setConnected: (connected) => set({ connected }),
}));

// Contact (CRM) State
export type ContactStatus = 'New Lead' | 'Prospect' | 'Customer' | 'Archived' | 'Lead' | 'Contacted' | 'Proposal' | 'Active' | 'Churned';
export type LeadTemperature = 'hot' | 'warm' | 'cold';
export type ContactSource = 'PDF Upload' | 'Business Card' | 'Website Widget' | 'Manual Entry' | 'Google Sheets' | 'Email' | 'Other';

// Message and Conversation Types for Unified CRM
export type MessageChannel = 'email' | 'whatsapp' | 'sms' | 'website' | 'phone' | 'linkedin' | 'slack';
export type MessageDirection = 'inbound' | 'outbound';
export type MessageStatus = 'sent' | 'delivered' | 'read' | 'failed' | 'pending';
export type MessageSentiment = 'positive' | 'neutral' | 'negative' | 'urgent';

export interface Message {
  id: string;
  contactId: string;
  channel: MessageChannel;
  direction: MessageDirection;
  content: string;
  subject?: string; // For emails
  from: string; // Email/phone/identifier
  to: string; // Email/phone/identifier
  timestamp: Date;
  status: MessageStatus;
  sentiment?: MessageSentiment;
  aiGenerated?: boolean; // Whether AI generated this response
  agentId?: string; // Which AI agent handled this
  metadata?: {
    threadId?: string; // For email threads
    messageId?: string; // External message ID
    attachments?: Array<{ name: string; url: string; type: string }>;
    [key: string]: any;
  };
  createdAt: Date;
  updatedAt: Date;
}

export interface Conversation {
  id: string;
  contactId: string;
  title: string; // Auto-generated from first message
  channel: MessageChannel;
  messages: Message[];
  lastMessageAt: Date;
  unreadCount: number;
  status: 'active' | 'archived' | 'resolved';
  assignedAgentId?: string;
  tags: string[];
  createdAt: Date;
  updatedAt: Date;
}

export interface ContactInteraction {
  id: string;
  contactId: string;
  type: 'email' | 'whatsapp' | 'sms' | 'call' | 'website_chat' | 'meeting' | 'note';
  channel: MessageChannel;
  direction: MessageDirection;
  summary: string; // AI-generated summary
  sentiment: MessageSentiment;
  timestamp: Date;
  messageId?: string; // Link to message if applicable
  metadata?: Record<string, any>;
}

export interface Contact {
  id: string;
  name: string;
  email: string;
  phone: string;
  company: string;
  status: ContactStatus;
  lastContact: Date;
  createdAt: Date;
  notes: { text: string; date: Date }[];
  tasks: { text: string; dueDate: Date; isCompleted: boolean }[];
  // AI-powered CRM fields
  source?: ContactSource;
  leadTemperature?: LeadTemperature;
  aiSummary?: string;
  lastInteraction?: string; // e.g., "AI Call - 2 days ago", "Email Sent - Yesterday"
}

interface ContactState {
  contacts: Contact[];
  addContact: (contactData: Omit<Contact, 'id' | 'createdAt' | 'notes' | 'tasks'>) => void;
  updateContact: (id: string, updates: Partial<Contact>) => void;
  deleteContact: (id: string) => void;
}

const createDummyContacts = (): Contact[] => {
  const contacts: Contact[] = [];
  const now = new Date();
  const statuses: ContactStatus[] = ['Lead', 'Contacted', 'Proposal', 'Active', 'Churned'];
  const firstNames = ['John', 'Jane', 'Alex', 'Emily', 'Chris', 'Katie'];
  const lastNames = ['Smith', 'Doe', 'Johnson', 'Williams', 'Brown', 'Davis'];
  const companies = ['Innovate Inc.', 'Solutions Co.', 'Synergy Corp.', 'Apex Enterprises', 'Quantum Tech'];

  for (let i = 0; i < 20; i++) {
    const createdAt = new Date(now.getTime() - Math.random() * 30 * 24 * 60 * 60 * 1000);
    const lastContact = new Date(createdAt.getTime() + Math.random() * (now.getTime() - createdAt.getTime()));
    contacts.push({
      id: `contact-${i}`,
      name: `${firstNames[i % firstNames.length]} ${lastNames[i % lastNames.length]}`,
      email: `user${i}@example.com`,
      phone: `(555) 123-45${i.toString().padStart(2, '0')}`,
      company: companies[i % companies.length],
      status: statuses[i % statuses.length],
      lastContact,
      createdAt,
      notes: [{ text: 'Initial discovery call scheduled.', date: createdAt }],
      tasks: [{ text: 'Follow up on proposal', dueDate: new Date(now.getTime() + 3 * 24 * 60 * 60 * 1000), isCompleted: false }],
    });
  }
  return contacts;
};

export const useContactStore = create<ContactState>()(
  persist(
    (set) => ({
      contacts: [], // Start with empty array instead of dummy data
      addContact: (contactData) => set((state) => ({
        contacts: [...state.contacts, {
          ...contactData,
          id: crypto.randomUUID(),
          createdAt: new Date(),
          notes: [],
          tasks: [],
        }],
      })),
      updateContact: (id, updates) => set((state) => ({
        contacts: state.contacts.map((contact) =>
          contact.id === id ? { ...contact, ...updates } : contact
        ),
      })),
      deleteContact: (id) => set((state) => ({
        contacts: state.contacts.filter((contact) => contact.id !== id),
      })),
    }),
    {
      name: 'contact-storage',
      // Custom serialization to handle Date objects
      storage: {
        getItem: (name) => {
          const str = localStorage.getItem(name);
          if (!str) return null;
          const { state } = JSON.parse(str);
          return {
            state: {
              ...state,
              contacts: state.contacts.map((contact: Contact) => ({
                ...contact,
                lastContact: new Date(contact.lastContact),
                createdAt: new Date(contact.createdAt),
                notes: contact.notes.map(note => ({ ...note, date: new Date(note.date) })),
                tasks: contact.tasks.map(task => ({ ...task, dueDate: new Date(task.dueDate) })),
              })),
            },
          };
        },
        setItem: (name, newValue) => {
          localStorage.setItem(name, JSON.stringify(newValue));
        },
        removeItem: (name) => localStorage.removeItem(name),
      },
    }
  )
);
// Fix: Add useOnboardingStore to manage onboarding state.
// Onboarding State
interface OnboardingState {
  hasCompletedOnboarding: boolean;
  currentStep: number;
  nextStep: () => void;
  finishTour: () => void;
  startTour: () => void;
}

export const useOnboardingStore = create<OnboardingState>()(
  persist(
    (set) => ({
      hasCompletedOnboarding: false,
      currentStep: 0,
      nextStep: () => set((state) => ({ currentStep: state.currentStep + 1 })),
      finishTour: () => set({ hasCompletedOnboarding: true, currentStep: 0 }),
      startTour: () => set({ hasCompletedOnboarding: false, currentStep: 0 }),
    }),
    {
      name: 'onboarding-storage',
    }
  )
);

// Developer Tools State
export interface ApiKey {
  id: string;
  name: string;
  key: string;
  created: string; // ISO date string
}

interface ApiKeyStore {
  keys: ApiKey[];
  addKey: (name: string) => ApiKey; // Return the new key
  deleteKey: (id: string) => void;
}

export const useApiKeyStore = create<ApiKeyStore>()(
  persist(
    (set, get) => ({
      keys: [
        { id: '1', name: 'Default Key', key: `sk-******************${crypto.randomUUID().slice(0, 4)}`, created: new Date().toISOString().split('T')[0] },
        { id: '2', name: 'Staging Key', key: `sk-******************${crypto.randomUUID().slice(0, 4)}`, created: new Date(Date.now() - 1000 * 60 * 60 * 24 * 30).toISOString().split('T')[0] },
      ],
      addKey: (name) => {
        const newKey: ApiKey = {
          id: crypto.randomUUID(),
          name,
          key: `sk-live-${crypto.randomUUID().replace(/-/g, '')}`, // A more realistic key format
          created: new Date().toISOString().split('T')[0],
        };
        set({ keys: [...get().keys, newKey] });
        return newKey;
      },
      deleteKey: (id) => set((state) => ({
        keys: state.keys.filter(key => key.id !== id),
      })),
    }),
    { name: 'api-key-storage' }
  )
);

export type WebhookEvent = 'call.completed' | 'call.failed' | 'contact.created';

export interface Webhook {
  id: string;
  url: string;
  event: WebhookEvent;
  status: 'active' | 'inactive';
}

interface WebhookStore {
  webhooks: Webhook[];
  addWebhook: (webhookData: Omit<Webhook, 'id' | 'status'>) => void;
  deleteWebhook: (id: string) => void;
  toggleWebhookStatus: (id: string) => void;
}

export const useWebhookStore = create<WebhookStore>()(
  persist(
    (set) => ({
      webhooks: [
        { id: '1', url: 'https://example.com/webhook/call-completed', event: 'call.completed', status: 'active' },
        { id: '2', url: 'https://example.com/webhook/call-failed', event: 'call.failed', status: 'inactive' },
      ],
      addWebhook: (webhookData) => set((state) => ({
        webhooks: [...state.webhooks, { ...webhookData, id: crypto.randomUUID(), status: 'active' }],
      })),
      deleteWebhook: (id) => set((state) => ({
        webhooks: state.webhooks.filter(webhook => webhook.id !== id),
      })),
      toggleWebhookStatus: (id) => set((state) => ({
        webhooks: state.webhooks.map(webhook =>
          webhook.id === id
            ? { ...webhook, status: webhook.status === 'active' ? 'inactive' : 'active' }
            : webhook
        ),
      })),
    }),
    { name: 'webhook-storage' }
  )
);